/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { generatePassword } from "services/userServices";
import {Button, Icon, useToast } from '@coreai/component-library';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from 'LanguageContext';
import { API_URLS } from 'services/config';
import axios from 'axios';

const GeneratePasswordForm = () => {
  const theme = useTheme();
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window?.location?.search);
  const email = queryParams.get("email");
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const isResetPassword = searchParams?.get('type') === 'reset';
  const isSetPassword = searchParams?.get('type') === 'set';

  const [isInvalidPassword, setIsInvalidPassword] = useState(false);

      const fetchCaptcha = async () => {
        try {
          const response = await axios.get(`${API_URLS.CAPTCH_LOAD}`, {
            responseType: 'arraybuffer',
          });
          const base64 = btoa(
            new Uint8Array(response.data)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          setCaptchaValue(`data:image/png;base64,${base64}`);
        } catch (error) {
          console.error('Captcha load error:', error);
        }
      };
    
      useEffect(() => {
        fetchCaptcha();
      }, []);
    
      const refreshCaptcha = () => {
       fetchCaptcha();
       setCaptchaInput('');
      };

  const containerStyle = css`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f4f6;
    padding: 1rem;
  `;

  const formStyle = css`
    background: white;
    padding: 2.5rem;
    border-radius: 0.5rem;
    box-shadow: 0px 4px 24px 0px #0000001A;
    width: 100%;
    max-width: 450px;
  `;

  const headingStyle = css`
    font-size: 30px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0%;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  `;

  const descriptionStyle = css`
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
    color: #3d5360;
    text-align: left;
    margin: 20px 0;

    @media (max-width: 768px) {
      margin: 12px 0;
    }
  `;

  const labelStyle = css`
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  `;

  const inputStyle = css`
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #acb8bd;
    border-radius: 0.5rem;
    margin-bottom: 1.25rem;
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}33;
    }
  `;

  const confirmPasswordStyle = css`
    margin-bottom: 4px;
  `;

  const buttonStyle = css`
    width: 100%;
    font-size: 16px;
    background-color: ${theme.colors.primary};
    color: white;
    padding: 0.5rem;
    border: none;
    border-radius: 1.25rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  `;

  const captchaMaindiv = css`
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 12px;
      
      img {
        height: 50px;
        width: 180px;
        border-radius: 6px;
        box-shadow: 0px 4px 24px 0px #0000001A;
      }
    `;
  
    const refreshIconStyle = css`
      cursor: pointer;
    `;

    useEffect(() => {
      const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_\-=`~+\[\]{};'"\\|,.<>\/?]).{8,20}$/;
      setIsInvalidPassword(!passwordRegex.test(newPassword));
    }, [newPassword]);
    
    const wrapperStyle = css`
      display: flex;
      gap: 4px;
      align-items: flex-start;
      padding: 0.5rem 0;
    `;
    
    const passwordValidStyle = css`
      font-size: 12px;
      color: ${
        newPassword?.length > 0
          ? isInvalidPassword
            ? theme.colors.red
            : theme.colors.primary
          : theme.colors.textMedium
      };
      margin-top: 0px;
      line-height: 20px;
    `;
    
    const iconStyle = css`
      width: 16px;
      height: 16px;
      color: ${
          newPassword?.length > 0
            ? isInvalidPassword
              ? theme.colors.red
              : theme.colors.primary
            : theme.colors.textMedium
        };
    `;    

  const handleSubmit = (e: any) => {
     e.preventDefault();
        try {

          if (newPassword !== confirmPassword) {
            alert('Passwords do not match');
             return;
           }
          const formData = new FormData();
          email && formData.append('email', email);
          formData.append('otp', otp);
          formData.append('captcha', captchaInput);
          formData.append('password', newPassword);

          generatePassword(formData).then((result: any) => {
              if(result.success){
                    toast({
                    variant: 'success',
                    title: result?.message,
                    });
                navigate(`/signin`);
              }
            }).catch((error: any) => {
              console.log({error},'updateUserConsent')
                toast({
                variant: 'error',
                title: error?.response?.data?.message,
                });
                refreshCaptcha();
            })
    
        } catch (error) {
          
        }

  };
  return (
    <div css={containerStyle}>
      <form css={formStyle} onSubmit={handleSubmit}>
        <h2 css={headingStyle}>{isResetPassword ? t('reset_password_title') : t('set_password_title')}</h2>
        <p css={descriptionStyle}>
          {t('reset_password_desc')}
        </p>

        <div>
          <label css={labelStyle}>{t('reset_password_otp')}</label>
          <input
            type="text"
            placeholder={t('reset_password_enter_otp')}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            css={inputStyle}
            required
          />
        </div>

        <div>
          <div css={captchaMaindiv}>
            <img src={captchaValue} alt="captach" />
            <Icon
                css={refreshIconStyle}
                id="captchabtn"
                onClick={() => refreshCaptcha()}
                name="refresh" 
                size="md"
            />
          </div>
          <label css={labelStyle}>{t('change_password_captcha')}</label>
          <input
            required
            type="text"
            name="captchaInput"
            data-testid="captchaInput"
            placeholder={t('reset_password_enter_captcha')}
            value={captchaInput}
            css={inputStyle}
            onChange={(event) => {
              setCaptchaInput(event.target.value);
            }}
            />
        </div>

        <div>
          <label css={labelStyle}>{t('reset_password_new')}</label>
          <input
            type="password"
            placeholder={t('update_password_enter_new')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            css={[inputStyle, confirmPasswordStyle]}
            required
          />
        </div>
        <div css={wrapperStyle}>
          <Icon name={`${isInvalidPassword ? "info-circle" : "check-circle"}`} size="sm" css={iconStyle}/>
          <p css={passwordValidStyle}>{t('password_policy')}</p>
        </div>

        <div>
          <label css={labelStyle}>{t('reset_password_confirm')}</label>
          <input
            type="password"
            placeholder={t('update_password_enter_confirm')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            css={inputStyle}
            required
          />
        </div>

        <Button type="submit" css={buttonStyle} disabled={ (newPassword !== confirmPassword) ||  otp ==="" || newPassword ==="" || confirmPassword === ""}>
          {t('update_password_btn')}
        </Button>
      </form>
    </div>
  );
};

export default GeneratePasswordForm;